/*
 * itv-x-post-app.js — X Post Studio
 *
 * Mirrors the structure of g-serp-app.js so the two tools read the same way:
 * a single `state` object, saveState/pushHistory/undo/redo, renderPreview(),
 * applyTemplate(index), and Pro derived from a signed token rather than held
 * as a flag.
 *
 * Rendering builds DOM NODES. There is no innerHTML anywhere that user text
 * can reach — display names, post bodies, poll options and Community Note
 * text are all set with textContent. itv-x-post-format.js segment() returns
 * DATA for exactly this reason: an entity segmenter that returned markup
 * would be an injection sink in a tool whose whole purpose is rendering
 * text somebody else typed.
 */
(function (window, document) {
    'use strict';

    var F = null;      // ITVXFormat, resolved at init
    var I = null;      // ITVXI18n,   resolved at init
    function t(k, v) { return I ? I.t(k, v) : k; }

    /* Placeholder avatar. A data URI on purpose — the tool this replaces
       pulled its placeholders from placehold.co, that host went unreachable,
       and every visitor got an error banner and a broken avatar on the site's
       second-best-converting page. Nothing in the mockup may depend on a
       third-party origin. */
    var BLANK_AVATAR =
        'data:image/svg+xml;utf8,' + encodeURIComponent(
            '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48">' +
            '<rect width="48" height="48" fill="#cfd9de"/>' +
            '<circle cx="24" cy="19" r="8" fill="#8899a6"/>' +
            '<path d="M8 46c2-9 8.5-13 16-13s14 4 16 13z" fill="#8899a6"/></svg>');

    window.itvXPost = {

        /* =================================================================
         * Licensing — mechanism only
         * =================================================================
         *
         * Pro is DERIVED from a server-signed, device-bound token. Read-only
         * getter on purpose: a plain `isPro: false` field can be set from
         * anywhere, which is how `localStorage.itech_pro_unlocked = 'true'`
         * used to grant Pro in the tool this pattern came from.
         */
        get isPro() {
            return !!(window.ITVLicense && window.ITVLicense.isPro());
        },

        /*
         * THE PAYWALL SWITCH, and the only one.
         *
         * While this is false every gate calls through to allowed and the
         * tool refuses nobody. That is deliberate and it is not an oversight:
         * the decision about what is free versus paid is being made from live
         * data by the owner, not here. Replicating an unproven policy across
         * four tools is how you get four tools carrying the same wrong
         * assumption.
         *
         * Flipping this to true is the entire policy change. Everything else
         * — the getter, the choke point, the per-action gate list — is built
         * and tested now, because mechanism is cheap today and expensive to
         * retrofit.
         */
        GATE_ENFORCED: false,

        /*
         * ANALYTICS. This tool sent nothing at all for months, while 'x-post'
         * sat on the events function's TOOLS allowlist - so the server read as
         * instrumented and the empty table read as "nobody uses it". The true
         * statement was that nobody was telling it.
         *
         * Payload shape is copied from the REAL track() calls in
         * itv-cyber-city.js, not from the endpoint's expectations. Writing a
         * client from what the server accepts is how the last outage stayed
         * hidden: the test then proves the server consistent with itself.
         *
         * WHAT IS SENT: allowlisted enum values only. name, source and outcome
         * are all fixed vocabularies on the server, so no post text, handle,
         * display name or uploaded image can leave the browser through here
         * even by mistake. That property is why `source` below is the generic
         * 'template' rather than the template's own name - per-category
         * granularity needs the 12 categories added to the server's SOURCES
         * allowlist first, and all three allowlists have to move together or
         * the row is kept and silently hollowed out.
         *
         * Never lets telemetry break the tool.
         */
        track: function (name, props, once) {
            try {
                if (window.ITVAnalytics && typeof window.ITVAnalytics.track === 'function') {
                    window.ITVAnalytics.track(name, props || {}, once ? { once: true } : undefined);
                }
            } catch (e) { /* telemetry is never load-bearing */ }
        },

        /*
         * The single choke point. Every gated action calls this and nothing
         * calls the licence layer directly.
         */
        gateAuthoring: function (action) {
            if (!this.GATE_ENFORCED) return true;
            if (this.isPro) return true;
            this.showUnlockPrompt(action);
            return false;
        },

        showUnlockPrompt: function () {
            /* No modal while GATE_ENFORCED is false — there is nothing to
               sell yet. Kept as a named seam so the policy change is one
               file, not a search across the codebase. */
            this.toast(t('tpl.applied'), 'info');
        },

        /* ================================================================= */

        state: null,
        history: [],
        historyIndex: -1,
        historyMax: 50,
        ignoreNextHistory: false,
        activeCategory: 'all',
        templateSearch: '',
        exportScale: 2,
        previewScale: 1,
        userSetScale: false,   /* set once the visitor moves the zoom slider */
        toastTimer: null,

        STORAGE_KEY: 'itvXPostState_v1',

        defaultState: function () {
            var now = new Date();
            return {
                theme: 'light',
                viewport: 'desktop',
                layout: 'detail',
                author: {
                    name: 'iTechVista',
                    handle: 'itechvista',
                    avatar: '',
                    badge: 'blue',
                    affiliateIcon: '',
                    protected: false
                },
                post: {
                    text: '',
                    media: [],
                    altBadge: false,
                    timestamp: now.toISOString(),
                    source: '',
                    dir: 'ltr'
                },
                metrics: {
                    replies: 128, reposts: 1102, quotes: 43,
                    likes: 4572, bookmarks: 210, views: 98400
                },
                flags: { liked: false, reposted: false, bookmarked: false,
                         promoted: false, pinned: false },
                quoted: null,
                note: null,
                poll: null,
                thread: [],
                replies: []
            };
        },

        /* =================================================================
         * Boot
         * ================================================================= */

        init: function () {
            F = window.ITVXFormat;
            I = window.ITVXI18n;
            if (!F || !I) {
                /* Fail loudly in the console but leave the page usable rather
                   than throwing during a defer'd script and leaving a dead
                   panel with no explanation. */
                console.error('[itv-x-post] format or i18n asset did not load');
                return;
            }

            var cfg = window.ITV_X_CONFIG || {};
            I.setLocale(cfg.locale || document.documentElement.lang || 'en');

            this.root = document.getElementById('itv-x-root');
            if (!this.root) return;
            this.stage = document.getElementById('itvx-stage');
            this.stageScale = document.getElementById('itvx-stage-scale');

            this.state = this.loadState() || this.defaultState();
            if (!this.state.post.text) {
                /* The opening post is a translated SAMPLE, not the compose
                   placeholder — "What is happening?" rendered as the body of a
                   posted tweet reads as a bug, because on X that string only
                   ever appears in an empty composer. A Russian visitor opens a
                   Russian example, not an English one. */
                this.state.post.text = t('post.text.sample');
            }

            I.apply(this.root);
            this.matchStorefrontScheme();
            this.renderTemplateCategories();
            this.renderTemplates();
            this.syncFormFromState();
            this.attachListeners();
            this.pushHistory();
            this.renderPreview();

            if (window.ITVLicense && window.ITVLicense.boot) {
                window.ITVLicense.boot();
            }
            this.loadFromURL();

            /* after the first paint, so the wrapper has a measured width */
            var self = this;
            this.fitPreviewToWrap();
            window.addEventListener('resize', function () {
                clearTimeout(self._fitT);
                self._fitT = setTimeout(function () { self.fitPreviewToWrap(); }, 120);
            });

            /* once:true - a session, not a pageview counter. The analytics
               client boots on `defer` like this app, so it is ready by now;
               track() tolerates it not being. */
            this.track('page_ready', { source: 'other', outcome: 'ready' }, true);
        },

        /* The storefront's own dark mode is a SEPARATE axis from the X theme
           being mocked. Editing a light-mode tweet on a dark storefront is a
           normal thing to want, so these must not be wired together. */
        matchStorefrontScheme: function () {
            var dark = false;
            try {
                var bg = getComputedStyle(document.body).backgroundColor || '';
                var m = bg.match(/(\d+),\s*(\d+),\s*(\d+)/);
                if (m) {
                    var lum = (0.299 * +m[1] + 0.587 * +m[2] + 0.114 * +m[3]);
                    dark = lum < 128;
                }
            } catch (e) { /* computed style unavailable: keep light */ }
            this.root.setAttribute('data-ui-scheme', dark ? 'dark' : 'light');
        },

        /* =================================================================
         * Persistence and history
         * ================================================================= */

        saveState: function () {
            try {
                localStorage.setItem(this.STORAGE_KEY, JSON.stringify(this.state));
            } catch (e) { /* private mode / quota: the tool still works */ }
            this.pushHistory();
        },

        loadState: function () {
            try {
                var raw = localStorage.getItem(this.STORAGE_KEY);
                if (!raw) return null;
                var s = JSON.parse(raw);
                return (s && s.author && s.post) ? s : null;
            } catch (e) { return null; }
        },

        pushHistory: function () {
            if (this.ignoreNextHistory) { this.ignoreNextHistory = false; return; }
            var snap = JSON.stringify(this.state);
            if (this.history[this.historyIndex] === snap) return;
            this.history = this.history.slice(0, this.historyIndex + 1);
            this.history.push(snap);
            if (this.history.length > this.historyMax) this.history.shift();
            this.historyIndex = this.history.length - 1;
            this.updateHistoryButtons();
        },

        undo: function () {
            if (this.historyIndex <= 0) return;
            this.historyIndex--;
            this.applySnapshot(this.history[this.historyIndex]);
        },

        redo: function () {
            if (this.historyIndex >= this.history.length - 1) return;
            this.historyIndex++;
            this.applySnapshot(this.history[this.historyIndex]);
        },

        applySnapshot: function (snap) {
            this.state = JSON.parse(snap);
            this.ignoreNextHistory = true;
            this.syncFormFromState();
            this.renderPreview();
            this.updateHistoryButtons();
            try { localStorage.setItem(this.STORAGE_KEY, snap); } catch (e) {}
        },

        updateHistoryButtons: function () {
            var u = document.getElementById('itvx-undo');
            var r = document.getElementById('itvx-redo');
            if (u) u.disabled = this.historyIndex <= 0;
            if (r) r.disabled = this.historyIndex >= this.history.length - 1;
        },

        /* =================================================================
         * DOM helpers — nodes, never HTML strings
         * ================================================================= */

        el: function (tag, cls, text) {
            var n = document.createElement(tag);
            if (cls) n.className = cls;
            if (text !== undefined && text !== null) n.textContent = text;
            return n;
        },

        icon: function (id, size) {
            var svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
            svg.setAttribute('width', size || 18);
            svg.setAttribute('height', size || 18);
            svg.setAttribute('aria-hidden', 'true');
            var use = document.createElementNS('http://www.w3.org/2000/svg', 'use');
            use.setAttribute('href', '#' + id);
            svg.appendChild(use);
            return svg;
        },

        /* =================================================================
         * Rendering
         * ================================================================= */

        renderPreview: function () {
            var s = this.state;
            var stage = this.stage;
            if (!stage) return;

            stage.setAttribute('data-x-theme', s.theme);
            stage.setAttribute('data-x-viewport', s.viewport);
            stage.setAttribute('data-x-layout', s.layout);
            stage.textContent = '';

            if (s.layout === 'detail') {
                stage.appendChild(this.renderDetailPost());
                (s.thread || []).forEach(function (p, i) {
                    stage.appendChild(this.renderFeedPost(this.threadAsPost(p), {
                        threadLine: i < s.thread.length - 1
                    }));
                }, this);
            } else {
                stage.appendChild(this.renderFeedPost(this.mainAsPost(), {
                    threadLine: (s.thread || []).length > 0
                }));
                (s.thread || []).forEach(function (p, i) {
                    stage.appendChild(this.renderFeedPost(this.threadAsPost(p), {
                        threadLine: i < s.thread.length - 1
                    }));
                }, this);
            }

            (s.replies || []).forEach(function (r, i) {
                stage.appendChild(this.renderFeedPost(this.replyAsPost(r), {
                    replyingTo: s.author.handle,
                    last: i === s.replies.length - 1
                }));
            }, this);

            this.applyPreviewScale();
        },

        mainAsPost: function () {
            var s = this.state;
            return {
                author: s.author, post: s.post, metrics: s.metrics,
                flags: s.flags, quoted: s.quoted, note: s.note, poll: s.poll
            };
        },

        threadAsPost: function (p) {
            return {
                author: this.state.author,
                post: p, metrics: p.metrics || { replies: 0, reposts: 0, likes: 0, views: 0 },
                flags: {}, quoted: null, note: null, poll: null
            };
        },

        replyAsPost: function (r) {
            return {
                author: r.author || this.state.author,
                post: r.post || r,
                metrics: r.metrics || { replies: 0, reposts: 0, likes: 0, views: 0 },
                flags: {}, quoted: null, note: null, poll: null
            };
        },

        /* ---- feed (timeline) layout ---- */
        renderFeedPost: function (p, opts) {
            opts = opts || {};
            var wrap = this.el('div', 'itv-x-post' + (opts.last ? ' itv-x-post--last' : ''));
            wrap.setAttribute('data-badge', p.author.badge || 'none');

            if (this.state.flags.pinned && p === undefined) { /* unreachable guard */ }

            var avCol = this.el('div', 'itv-x-avatar-col');
            avCol.appendChild(this.avatarNode(p.author));
            if (opts.threadLine) avCol.appendChild(this.el('div', 'itv-x-thread-line'));
            wrap.appendChild(avCol);

            var main = this.el('div');
            var head = this.el('div', 'itv-x-post-head');
            this.fillHead(head, p.author, true, p.post.timestamp);
            main.appendChild(head);

            if (opts.replyingTo) {
                var rt = this.el('div', 'itv-x-replying');
                rt.appendChild(document.createTextNode(
                    t('reply.replyingTo', { handle: '' }).replace('@', '').trim() + ' '));
                var at = this.el('b', null, '@' + opts.replyingTo);
                rt.appendChild(at);
                main.appendChild(rt);
            }

            main.appendChild(this.bodyNode(p.post));
            if (p.post.media && p.post.media.length) main.appendChild(this.mediaNode(p.post));
            if (p.poll) main.appendChild(this.pollNode(p.poll));
            if (p.quoted) main.appendChild(this.quoteNode(p.quoted));
            if (p.note) main.appendChild(this.noteNode(p.note));
            main.appendChild(this.actionsNode(p, false));

            if (this.state.flags.promoted && !opts.replyingTo) {
                main.appendChild(this.el('div', 'itv-x-promoted', t('x.promoted')));
            }

            wrap.appendChild(main);
            return wrap;
        },

        /* ---- detail (single post) layout ---- */
        renderDetailPost: function () {
            var s = this.state;
            var p = this.mainAsPost();
            var wrap = this.el('div', 'itv-x-post itv-x-post--main');
            wrap.setAttribute('data-badge', s.author.badge || 'none');

            if (s.flags.pinned) {
                var pin = this.el('div', 'itv-x-context', t('x.pinned'));
                wrap.appendChild(pin);
            }

            /* Detail view puts the account on its own row above the body,
               rather than in the avatar gutter. */
            var idRow = this.el('div');
            idRow.style.display = 'flex';
            idRow.style.gap = '12px';
            idRow.style.alignItems = 'center';
            idRow.appendChild(this.avatarNode(s.author));

            var names = this.el('div');
            names.style.minWidth = '0';
            var line1 = this.el('div', 'itv-x-post-head');
            this.fillHead(line1, s.author, false, null);
            names.appendChild(line1);
            names.appendChild(this.el('div', 'itv-x-handle', '@' + s.author.handle));
            idRow.appendChild(names);
            wrap.appendChild(idRow);

            wrap.appendChild(this.bodyNode(s.post));
            if (s.post.media && s.post.media.length) wrap.appendChild(this.mediaNode(s.post));
            if (s.poll) wrap.appendChild(this.pollNode(s.poll));
            if (s.quoted) wrap.appendChild(this.quoteNode(s.quoted));
            if (s.note) wrap.appendChild(this.noteNode(s.note));

            /* Absolute timestamp line — "9:41 AM · Mar 14, 2026 · 98.4K Views" */
            var ts = this.el('div', 'itv-x-timestamp-line');
            ts.appendChild(document.createTextNode(
                F.absoluteTimestamp(s.post.timestamp, I.locale())));
            if (s.post.source) {
                ts.appendChild(document.createTextNode(' · '));
                var src = this.el('span');
                src.style.color = 'var(--x-accent)';
                src.textContent = s.post.source;
                ts.appendChild(src);
            }
            if (s.metrics.views) {
                ts.appendChild(document.createTextNode(' · '));
                var vb = this.el('b', null, F.abbreviateCount(s.metrics.views, I.locale()));
                vb.style.color = 'var(--x-text)';
                ts.appendChild(vb);
                ts.appendChild(document.createTextNode(' ' + t('x.views')));
            }
            wrap.appendChild(ts);

            wrap.appendChild(this.metricsStripNode(s.metrics));
            wrap.appendChild(this.actionsNode(p, true));
            return wrap;
        },

        avatarNode: function (author) {
            var img = document.createElement('img');
            img.className = 'itv-x-avatar';
            img.src = author.avatar || BLANK_AVATAR;
            img.alt = '';
            /* A user-supplied avatar that fails to decode must not leave a
               broken-image glyph in an exported PNG. */
            img.onerror = function () { this.src = BLANK_AVATAR; };
            return img;
        },

        fillHead: function (head, author, withHandleAndTime, timestamp) {
            head.appendChild(this.el('span', 'itv-x-name', author.name || ' '));

            if (author.badge && author.badge !== 'none' && author.badge !== 'affiliate') {
                var b = this.icon('itvx-i-verified', 16);
                b.setAttribute('class', 'itv-x-badge');
                b.setAttribute('data-tier', author.badge);
                head.appendChild(b);
            }
            if (author.badge === 'affiliate' && author.affiliateIcon) {
                var a = document.createElement('img');
                a.className = 'itv-x-affiliate';
                a.src = author.affiliateIcon;
                a.alt = '';
                head.appendChild(a);
            }
            if (author.protected) {
                var lock = this.icon('itvx-i-lock', 14);
                lock.setAttribute('class', 'itv-x-badge');
                lock.style.color = 'var(--x-muted)';
                head.appendChild(lock);
            }
            if (withHandleAndTime) {
                head.appendChild(this.el('span', 'itv-x-handle', '@' + author.handle));
                head.appendChild(this.el('span', 'itv-x-dot', '·'));
                head.appendChild(this.el('span', 'itv-x-time',
                    F.relativeTimestamp(timestamp, new Date(), I.locale())));
            }
        },

        /*
         * Post body. Entities are rendered from segment() DATA into real
         * nodes — a mention becomes a coloured span with textContent, never
         * an interpolated HTML string.
         */
        bodyNode: function (post) {
            var body = this.el('div', 'itv-x-body');
            body.setAttribute('dir', post.dir || 'ltr');
            var parts = F.segment(post.text || '');
            parts.forEach(function (seg) {
                if (seg.type === 'text') {
                    body.appendChild(document.createTextNode(seg.value));
                } else {
                    var span = this.el('span', 'itv-x-link',
                        seg.type === 'url' ? seg.display : seg.value);
                    body.appendChild(span);
                }
            }, this);
            return body;
        },

        mediaNode: function (post) {
            var n = Math.min(post.media.length, 4);
            var grid = this.el('div', 'itv-x-media');
            grid.setAttribute('data-count', String(n));
            for (var i = 0; i < n; i++) {
                var cell = this.el('div', 'itv-x-media-cell');
                var img = document.createElement('img');
                img.src = post.media[i].src || post.media[i];
                img.alt = post.media[i].alt || '';
                cell.appendChild(img);
                if (post.altBadge) cell.appendChild(this.el('span', 'itv-x-alt', 'ALT'));
                grid.appendChild(cell);
            }
            return grid;
        },

        quoteNode: function (q) {
            var box = this.el('div', 'itv-x-quote');
            var head = this.el('div', 'itv-x-quote-head');
            var av = this.avatarNode(q.author);
            head.appendChild(av);
            head.appendChild(this.el('span', 'itv-x-name', q.author.name || ' '));
            if (q.author.badge && q.author.badge !== 'none') {
                var b = this.icon('itvx-i-verified', 15);
                b.setAttribute('class', 'itv-x-badge');
                b.setAttribute('data-tier', q.author.badge);
                head.appendChild(b);
            }
            head.appendChild(this.el('span', 'itv-x-handle', '@' + q.author.handle));
            head.appendChild(this.el('span', 'itv-x-dot', '·'));
            head.appendChild(this.el('span', 'itv-x-time',
                F.relativeTimestamp(q.post.timestamp, new Date(), I.locale())));
            box.appendChild(head);
            box.appendChild(this.bodyNode(q.post));
            if (q.post.media && q.post.media.length) box.appendChild(this.mediaNode(q.post));
            return box;
        },

        /* Community Note. Grey card, not red — it is context, not a warning,
           and the generators that colour it red get the tone visibly wrong. */
        noteNode: function (note) {
            var box = this.el('div', 'itv-x-note');
            var head = this.el('div', 'itv-x-note-head');
            head.appendChild(this.icon('itvx-i-note', 16));
            head.appendChild(this.el('span', null, note.headline || t('x.noteHeadline')));
            box.appendChild(head);
            box.appendChild(this.el('div', 'itv-x-note-body', note.body || ''));
            if (note.helpful !== false) {
                box.appendChild(this.el('div', 'itv-x-note-helpful', t('x.noteHelpful')));
            }
            return box;
        },

        pollNode: function (poll) {
            var box = this.el('div', 'itv-x-poll');
            var opts = poll.options || [];
            var total = opts.reduce(function (a, o) { return a + (+o.votes || 0); }, 0) || 1;
            var max = opts.reduce(function (a, o) { return Math.max(a, +o.votes || 0); }, 0);

            opts.forEach(function (o) {
                var pct = ((+o.votes || 0) / total) * 100;
                var row = this.el('div', 'itv-x-poll-opt');
                if ((+o.votes || 0) === max && max > 0) row.setAttribute('data-winner', 'true');
                var fill = this.el('div', 'itv-x-poll-fill');
                fill.style.width = pct.toFixed(1) + '%';
                row.appendChild(fill);
                row.appendChild(this.el('span', 'itv-x-poll-label', o.label || ''));
                row.appendChild(this.el('span', 'itv-x-poll-pct', pct.toFixed(1) + '%'));
                box.appendChild(row);
            }, this);

            var meta = this.el('div', 'itv-x-poll-meta');
            meta.textContent = t('x.pollVotes', {
                n: F.abbreviateCount(poll.totalVotes || total, I.locale())
            }) + ' · ' + (poll.ended ? t('x.pollFinal') : t('x.pollLeft', { t: poll.remaining || '1h' }));
            box.appendChild(meta);
            return box;
        },

        metricsStripNode: function (m) {
            var strip = this.el('div', 'itv-x-metrics-strip');
            var pairs = [
                [m.reposts, 'x.retweets'], [m.quotes, 'x.quotes'],
                [m.likes, 'x.likes'], [m.bookmarks, 'x.bookmarks']
            ];
            pairs.forEach(function (p) {
                if (!p[0]) return;
                var span = this.el('span');
                span.appendChild(this.el('b', null, F.abbreviateCount(p[0], I.locale())));
                span.appendChild(document.createTextNode(' ' + t(p[1])));
                strip.appendChild(span);
            }, this);
            return strip;
        },

        actionsNode: function (p, isDetail) {
            var bar = this.el('div', 'itv-x-actions');
            var m = p.metrics || {};
            var f = p.flags || {};
            var items = [
                { kind: 'reply', icon: 'itvx-i-reply', n: m.replies, on: false },
                { kind: 'repost', icon: 'itvx-i-repost', n: m.reposts, on: !!f.reposted },
                { kind: 'like', icon: f.liked ? 'itvx-i-like-filled' : 'itvx-i-like', n: m.likes, on: !!f.liked },
                { kind: 'views', icon: 'itvx-i-views', n: m.views, on: false },
                { kind: 'bookmark', icon: f.bookmarked ? 'itvx-i-bookmark-filled' : 'itvx-i-bookmark', n: null, on: !!f.bookmarked },
                { kind: 'share', icon: 'itvx-i-share', n: null, on: false }
            ];
            items.forEach(function (it) {
                var a = this.el('span', 'itv-x-action');
                a.setAttribute('data-kind', it.kind);
                if (it.on) a.setAttribute('data-on', 'true');
                a.appendChild(this.icon(it.icon, 18));
                /* The detail view shows icons WITHOUT counts — the counts
                   live in the strip above it. Showing both is the most
                   common structural error in the category. */
                if (it.n && !isDetail) {
                    a.appendChild(this.el('span', null, F.abbreviateCount(it.n, I.locale())));
                }
                bar.appendChild(a);
            }, this);
            return bar;
        },

        applyPreviewScale: function () {
            if (!this.stageScale) return;
            /* Origin must be top-left for the compensation below to line up.
               At scale 1 it makes no difference; scaled down, it means the card
               fills the box from the corner instead of being centred with the
               overflow split across both edges. */
            this.stageScale.style.transformOrigin = 'top left';
            this.stageScale.style.transform = 'scale(' + this.previewScale + ')';

            /* Transform does not affect LAYOUT — the element still occupies its
               unscaled box. The height was already compensated here; the WIDTH
               was not, and that was a real hole: after auto-fit made the card
               visually fit a 390px phone, the wrapper still reported
               scrollWidth 490 against clientWidth 342 and scrolled 148px into
               empty space. Visually correct, still draggable to nowhere. */
            var h = this.stage.offsetHeight * this.previewScale;
            var w = this.stage.offsetWidth * this.previewScale;
            this.stageScale.style.height = h + 'px';
            this.stageScale.style.width = w + 'px';
        },

        /*
         * FIT THE CARD TO ITS CONTAINER ON LOAD AND ON RESIZE.
         *
         * The stage is a fixed 598px — the width a real X card is composed at —
         * and previewScale defaulted to 1 with no code anywhere that measured
         * the container. On a 390px phone the wrapper is ~342px, so the card
         * rendered at 598 inside it: 43% of the visitor's own post off-screen at
         * first paint, on first load, before they had touched anything.
         *
         * Every automated check passed, including the cutover's 22/22, because
         * the overflow is CONTAINED — .itv-x-stage-wrap is overflow-x:auto, so
         * page-level scrollWidth never exceeds innerWidth and nothing at the
         * document level looks wrong. The page was not broken; the output was
         * just mostly invisible. A nested scroller hides a layout failure from
         * every check written at page scope.
         *
         * The only remedy that existed was the zoom slider — 2,270px further
         * down the page, a 16px-tall range input. A visitor whose card is
         * clipped at first paint had no visible way to fix it.
         *
         * The user stays in charge once they choose: touching the slider sets
         * userSetScale and auto-fitting stops for the rest of the session. An
         * auto-fit that keeps overriding a deliberate zoom is its own defect.
         */
        fitPreviewToWrap: function () {
            if (this.userSetScale || !this.stage || !this.stageScale) return;
            var wrap = this.stageScale.parentElement;
            if (!wrap) return;
            /* clientWidth INCLUDES padding, so fitting against it leaves the
               card overflowing by exactly the horizontal padding — measured as
               20px of residual scroll after the first version of this fix. Fit
               against the content box. */
            var cs = window.getComputedStyle(wrap);
            var pad = (parseFloat(cs.paddingLeft) || 0) + (parseFloat(cs.paddingRight) || 0);
            var avail = wrap.clientWidth - pad;
            var natural = this.stage.offsetWidth;
            if (avail <= 0 || !natural) return;          /* hidden or not laid out yet */

            var fit = natural > avail ? avail / natural : 1;

            /* The slider's floor is 50%. Clamp to it rather than moving the
               control somewhere it cannot represent — a slider showing a value
               it did not produce is worse than a card that still scrolls. */
            var slider = document.getElementById('itvx-scale');
            var min = slider ? (+slider.min || 40) / 100 : 0.4;

            /* Snap DOWN to the slider's step so the control and the actual
               scale agree exactly. Rounding to the nearest step made the slider
               read 50 while the scale was 0.48 — the control displaying a value
               it did not produce, which is the thing the clamp below exists to
               avoid. Down rather than nearest, because up would re-introduce
               overflow. */
            if (slider) {
                var step = +slider.step || 1;
                fit = Math.floor((fit * 100) / step) * step / 100;
            }
            if (fit < min) fit = min;

            if (Math.abs(fit - this.previewScale) < 0.005) return;
            this.previewScale = fit;
            if (slider) slider.value = Math.round(fit * 100);
            this.applyPreviewScale();
        },

        /* =================================================================
         * Character counter
         * ================================================================= */

        updateCount: function () {
            var box = document.getElementById('itvx-count');
            var txt = document.getElementById('itvx-count-text');
            if (!box || !txt) return;
            var used = F.weightedLength(this.state.post.text || '');
            var left = F.CFG.maxWeightedLength - used;
            if (left < 0) {
                box.setAttribute('data-state', 'over');
                txt.textContent = t('count.over', { n: -left });
            } else {
                box.setAttribute('data-state', left <= 20 ? 'warn' : 'ok');
                txt.textContent = t('count.remaining', { n: left });
            }
        },

        /* =================================================================
         * Listeners
         * =================================================================
         *
         * ONE place binds inputs, and it takes the gate flag. This is
         * structural on purpose. The failure it prevents is the one that
         * cost the SERP work real time: a gate was added to the obviously
         * named editor function while the actual live path was a listener
         * bound straight to the textarea, so the gate guarded nothing and
         * every natural test still passed. With a single binder there is no
         * second path for a listener to arrive by.
         */
        bind: function (id, event, apply, opts) {
            var el = document.getElementById(id);
            if (!el) return;
            var self = this;
            opts = opts || {};
            el.addEventListener(event, function (e) {
                if (opts.gated && !self.gateAuthoring(id)) {
                    self.syncFormFromState();       // revert the typed change
                    return;
                }
                apply.call(self, e.target, e);
                self.saveState();
                self.renderPreview();
            });
        },

        attachListeners: function () {
            var self = this;

            // ---- account ----
            this.bind('itvx-name', 'input', function (el) {
                this.state.author.name = el.value;
            });
            this.bind('itvx-handle', 'input', function (el) {
                var clean = F.normalizeHandle(el.value);
                this.state.author.handle = clean;
                if (el.value !== clean) el.value = clean;
            });
            this.bind('itvx-badge', 'change', function (el) {
                this.state.author.badge = el.value;
            });
            this.bind('itvx-protected', 'change', function (el) {
                this.state.author.protected = el.checked;
            });

            // ---- post ----
            this.bind('itvx-text', 'input', function (el) {
                this.state.post.text = el.value;
                this.updateCount();
            });
            this.bind('itvx-time', 'change', function (el) {
                if (el.value) this.state.post.timestamp = new Date(el.value).toISOString();
            });
            this.bind('itvx-source', 'input', function (el) {
                this.state.post.source = el.value;
            });
            this.bind('itvx-dir', 'change', function (el) {
                this.state.post.dir = el.value;
            });
            this.bind('itvx-altbadge', 'change', function (el) {
                this.state.post.altBadge = el.checked;
            });

            // ---- metrics ----
            ['replies', 'reposts', 'quotes', 'likes', 'bookmarks', 'views'].forEach(function (k) {
                self.bind('itvx-m-' + k, 'input', function (el) {
                    this.state.metrics[k] = Math.max(0, parseInt(el.value, 10) || 0);
                });
            });
            ['liked', 'reposted', 'bookmarked', 'promoted', 'pinned'].forEach(function (k) {
                self.bind('itvx-' + k, 'change', function (el) {
                    this.state.flags[k] = el.checked;
                });
            });

            // ---- uploads ----
            var av = document.getElementById('itvx-avatar');
            if (av) av.addEventListener('change', function (e) {
                self.readImage(e.target.files[0], function (data) {
                    self.state.author.avatar = data;
                    self.saveState(); self.renderPreview();
                });
            });
            var med = document.getElementById('itvx-media');
            if (med) med.addEventListener('change', function (e) {
                var files = Array.prototype.slice.call(e.target.files, 0, 4);
                var loaded = [];
                var pending = files.length;
                if (!pending) return;
                files.forEach(function (f, i) {
                    self.readImage(f, function (data) {
                        loaded[i] = { src: data, alt: '' };
                        if (--pending === 0) {
                            self.state.post.media = loaded.filter(Boolean);
                            self.saveState(); self.renderPreview();
                        }
                    }, function () { if (--pending === 0) { self.renderPreview(); } });
                });
            });

            // ---- segmented controls ----
            this.bindSeg('itvx-seg-theme', function (v) { this.state.theme = v; });
            this.bindSeg('itvx-seg-viewport', function (v) { this.state.viewport = v; });
            this.bindSeg('itvx-seg-layout', function (v) { this.state.layout = v; });
            this.bindSeg('itvx-seg-exportscale', function (v) { this.exportScale = +v; }, true);

            var scale = document.getElementById('itvx-scale');
            if (scale) scale.addEventListener('input', function (e) {
                self.userSetScale = true;   /* their choice wins from here on */
                self.previewScale = (+e.target.value) / 100;
                self.applyPreviewScale();
            });

            // ---- panels ----
            Array.prototype.forEach.call(
                this.root.querySelectorAll('.itv-x-panel-head'), function (head) {
                    head.addEventListener('click', function () {
                        var panel = head.closest('.itv-x-panel');
                        var open = panel.getAttribute('data-open') === 'true';
                        panel.setAttribute('data-open', open ? 'false' : 'true');
                        head.setAttribute('aria-expanded', open ? 'false' : 'true');
                    });
                });

            // ---- history ----
            var u = document.getElementById('itvx-undo');
            if (u) u.addEventListener('click', function () { self.undo(); });
            var r = document.getElementById('itvx-redo');
            if (r) r.addEventListener('click', function () { self.redo(); });
            var rs = document.getElementById('itvx-reset');
            if (rs) rs.addEventListener('click', function () {
                self.state = self.defaultState();
                self.state.post.text = t('post.text.sample');
                self.syncFormFromState(); self.saveState(); self.renderPreview();
            });

            // ---- extras ----
            this.bindClick('itvx-add-quote', function () { this.toggleQuote(); });
            this.bindClick('itvx-add-note', function () { this.toggleNote(); });
            this.bindClick('itvx-add-poll', function () { this.togglePoll(); });
            this.bindClick('itvx-add-thread', function () { this.addThreadPost(); });
            this.bindClick('itvx-add-reply', function () { this.addReply(); });

            // ---- templates ----
            var srch = document.getElementById('itvx-tpl-search');
            if (srch) srch.addEventListener('input', function (e) {
                self.templateSearch = e.target.value.toLowerCase();
                self.renderTemplates();
            });

            // ---- export ----
            this.bindClick('itvx-export-png', function () { this.exportPNG(); });
            this.bindClick('itvx-export-copy', function () { this.copyImage(); });
            this.bindClick('itvx-export-link', function () { this.copyShareLink(); });
        },

        bindClick: function (id, fn) {
            var el = document.getElementById(id);
            if (!el) return;
            var self = this;
            el.addEventListener('click', function () { fn.call(self); });
        },

        bindSeg: function (id, apply, noRender) {
            var group = document.getElementById(id);
            if (!group) return;
            var self = this;
            group.addEventListener('click', function (e) {
                var btn = e.target.closest('button[data-value]');
                if (!btn) return;
                Array.prototype.forEach.call(group.querySelectorAll('button'), function (b) {
                    b.setAttribute('aria-pressed', String(b === btn));
                });
                apply.call(self, btn.getAttribute('data-value'));
                if (!noRender) { self.saveState(); self.renderPreview(); }
            });
        },

        readImage: function (file, ok, fail) {
            var self = this;
            if (!file) return;
            if (file.size > 10 * 1024 * 1024) {
                this.toast(t('err.imageBig'), 'error');
                if (fail) fail();
                return;
            }
            var reader = new FileReader();
            reader.onload = function () { ok(reader.result); };
            reader.onerror = function () {
                self.toast(t('err.image'), 'error');
                if (fail) fail();
            };
            reader.readAsDataURL(file);
        },

        /* =================================================================
         * Extras
         * ================================================================= */

        toggleQuote: function () {
            if (!this.gateAuthoring('quote')) return;
            this.state.quoted = this.state.quoted ? null : {
                author: { name: 'Reuters', handle: 'Reuters', avatar: '', badge: 'blue' },
                post: { text: '', media: [], timestamp: new Date(Date.now() - 36e5).toISOString(), dir: 'ltr' }
            };
            this.saveState(); this.renderExtrasEditors(); this.renderPreview();
        },

        toggleNote: function () {
            if (!this.gateAuthoring('note')) return;
            this.state.note = this.state.note ? null : {
                headline: t('x.noteHeadline'), body: '', helpful: true
            };
            this.saveState(); this.renderExtrasEditors(); this.renderPreview();
        },

        togglePoll: function () {
            if (!this.gateAuthoring('poll')) return;
            this.state.poll = this.state.poll ? null : {
                options: [
                    { label: '', votes: 620 },
                    { label: '', votes: 380 }
                ],
                totalVotes: 1000, ended: false, remaining: '5h'
            };
            this.saveState(); this.renderExtrasEditors(); this.renderPreview();
        },

        addThreadPost: function () {
            if (!this.gateAuthoring('thread')) return;
            this.state.thread.push({
                text: '', media: [], dir: 'ltr',
                timestamp: new Date().toISOString(),
                metrics: { replies: 0, reposts: 0, likes: 0, views: 0 }
            });
            this.saveState(); this.renderExtrasEditors(); this.renderPreview();
        },

        addReply: function () {
            if (!this.gateAuthoring('reply')) return;
            this.state.replies.push({
                author: { name: '', handle: 'user', avatar: '', badge: 'none' },
                post: { text: '', media: [], dir: 'ltr',
                        timestamp: new Date().toISOString() },
                metrics: { replies: 0, reposts: 0, likes: 0, views: 0 }
            });
            this.saveState(); this.renderReplyEditors(); this.renderPreview();
        },

        /* Editors for the composite blocks are built from state so adding a
           reply cannot desync the form from the model. */
        renderExtrasEditors: function () {
            var host = document.getElementById('itvx-extras-editors');
            if (!host) return;
            host.textContent = '';
            var s = this.state, self = this;

            if (s.note) {
                host.appendChild(this.fieldTextarea(t('extra.note.body'), s.note.body,
                    function (v) { s.note.body = v; }));
            }
            if (s.quoted) {
                host.appendChild(this.fieldText(t('extra.quote') + ' — ' + t('author.name'),
                    s.quoted.author.name, function (v) { s.quoted.author.name = v; }));
                host.appendChild(this.fieldText(t('extra.quote') + ' — ' + t('author.handle'),
                    s.quoted.author.handle, function (v) { s.quoted.author.handle = F.normalizeHandle(v); }));
                host.appendChild(this.fieldTextarea(t('extra.quote') + ' — ' + t('post.text'),
                    s.quoted.post.text, function (v) { s.quoted.post.text = v; }));
            }
            if (s.poll) {
                s.poll.options.forEach(function (o, i) {
                    host.appendChild(self.fieldText(t('extra.poll.option', { n: i + 1 }),
                        o.label, function (v) { o.label = v; }));
                });
                host.appendChild(this.fieldNumber(t('extra.poll.votes'), s.poll.totalVotes,
                    function (v) { s.poll.totalVotes = v; }));
            }
            s.thread.forEach(function (p, i) {
                host.appendChild(self.fieldTextarea(t('extra.thread') + ' ' + (i + 2),
                    p.text, function (v) { p.text = v; }));
            });
        },

        renderReplyEditors: function () {
            var host = document.getElementById('itvx-reply-list');
            if (!host) return;
            host.textContent = '';
            var self = this;
            this.state.replies.forEach(function (r, i) {
                var box = self.el('div');
                box.style.borderTop = '1px solid var(--itv-ui-border)';
                box.style.paddingTop = '10px';
                box.style.marginTop = '10px';
                box.appendChild(self.fieldText(t('author.name'), r.author.name,
                    function (v) { r.author.name = v; }));
                box.appendChild(self.fieldText(t('author.handle'), r.author.handle,
                    function (v) { r.author.handle = F.normalizeHandle(v); }));
                box.appendChild(self.fieldTextarea(t('post.text'), r.post.text,
                    function (v) { r.post.text = v; }));
                var rm = self.el('button', 'itv-x-btn itv-x-btn--ghost', t('reply.remove'));
                rm.type = 'button';
                rm.addEventListener('click', function () {
                    self.state.replies.splice(i, 1);
                    self.saveState(); self.renderReplyEditors(); self.renderPreview();
                });
                box.appendChild(rm);
                host.appendChild(box);
            });
        },

        /* Small field factories. Every one routes its change through the same
           gate + save + render path as bind(), so a composite editor cannot
           become an ungated side door. */
        makeField: function (labelText, node, onInput) {
            var wrap = this.el('div', 'itv-x-field');
            var lab = this.el('label', 'itv-x-label', labelText);
            wrap.appendChild(lab);
            wrap.appendChild(node);
            var self = this;
            node.addEventListener('input', function () {
                if (!self.gateAuthoring('composite')) { return; }
                onInput(node.value);
                self.saveState();
                self.renderPreview();
            });
            return wrap;
        },

        fieldText: function (label, value, onInput) {
            var i = this.el('input', 'itv-x-input');
            i.type = 'text'; i.value = value || '';
            return this.makeField(label, i, onInput);
        },

        fieldNumber: function (label, value, onInput) {
            var i = this.el('input', 'itv-x-input');
            i.type = 'number'; i.min = '0'; i.value = value || 0;
            return this.makeField(label, i, function (v) { onInput(parseInt(v, 10) || 0); });
        },

        fieldTextarea: function (label, value, onInput) {
            var i = this.el('textarea', 'itv-x-textarea');
            i.value = value || '';
            return this.makeField(label, i, onInput);
        },

        /* =================================================================
         * Templates
         * ================================================================= */

        /*
         * Templates, localised where a native version exists.
         *
         * itv-x-post-templates-i18n.js carries NATIVE content for 14 of the 50
         * — not translations. The bar is "would a Russian speaker believe a
         * Russian wrote this", which a correct translation of an American news
         * story fails. The other 36 stay English on purpose: 36 stilted samples
         * would damage that impression more than 36 openly English ones.
         *
         * Localised templates sort FIRST in a non-English locale, so the first
         * thing a Russian visitor sees is the set that was made for them.
         */
        _tplCache: null,
        _tplCacheLocale: null,

        templates: function () {
            var base = window.ITV_X_TEMPLATES || [];
            var loc = I ? I.locale() : 'en';

            /*
             * MEMOISED, and not as an optimisation.
             *
             * renderTemplates() finds a card's index with
             * templates().indexOf(tp). Rebuilding the array on every call
             * returns fresh objects each time, so that lookup returns -1 and
             * every template silently stops applying — the cards still render,
             * still hover, still click, and do nothing. Returning a stable
             * array keeps object identity, which is what indexOf compares.
             */
            if (this._tplCache && this._tplCacheLocale === loc) return this._tplCache;

            var pack = (window.ITV_X_TEMPLATES_I18N || {})[loc];
            if (!pack) {
                this._tplCache = base;
                this._tplCacheLocale = loc;
                return base;
            }

            var out = base.map(function (tp) {
                var over = pack[tp.id];
                if (!over) return tp;
                return {
                    id: tp.id,
                    category: tp.category,
                    name: over.name || tp.name,
                    description: over.description || tp.description,
                    localised: true,
                    state: this.deepMerge(tp.state, over.state || {})
                };
            }, this);

            out.sort(function (a, b) {
                return (b.localised ? 1 : 0) - (a.localised ? 1 : 0);
            });
            this._tplCache = out;
            this._tplCacheLocale = loc;
            return out;
        },

        /*
         * Merge a partial over a base without mutating either. Arrays are
         * merged BY INDEX rather than replaced, so a locale override can
         * change one reply's text without restating the whole chain — and an
         * override array shorter than the base keeps the remaining entries
         * instead of truncating them.
         */
        deepMerge: function (base, over) {
            if (Array.isArray(base)) {
                var arr = base.map(function (x) { return this.deepMerge(x, {}); }, this);
                if (Array.isArray(over)) {
                    over.forEach(function (o, i) {
                        arr[i] = (i < base.length) ? this.deepMerge(base[i], o) : o;
                    }, this);
                }
                return arr;
            }
            if (base && typeof base === 'object') {
                var out = {};
                Object.keys(base).forEach(function (k) { out[k] = this.deepMerge(base[k], undefined); }, this);
                if (over && typeof over === 'object') {
                    Object.keys(over).forEach(function (k) {
                        out[k] = (k in out) ? this.deepMerge(out[k], over[k]) : over[k];
                    }, this);
                }
                return out;
            }
            return over === undefined ? base : over;
        },

        renderTemplateCategories: function () {
            var host = document.getElementById('itvx-tpl-cats');
            if (!host) return;
            host.textContent = '';
            var cats = ['all'];
            this.templates().forEach(function (tp) {
                if (cats.indexOf(tp.category) === -1) cats.push(tp.category);
            });
            var self = this;
            cats.forEach(function (c) {
                var b = self.el('button', 'itv-x-tpl-cat',
                    c === 'all' ? t('tpl.all') : t('cat.' + c));
                b.type = 'button';
                b.setAttribute('data-cat', c);
                b.setAttribute('aria-pressed', String(c === self.activeCategory));
                b.addEventListener('click', function () {
                    self.activeCategory = c;
                    Array.prototype.forEach.call(host.querySelectorAll('button'), function (x) {
                        x.setAttribute('aria-pressed', String(x === b));
                    });
                    self.renderTemplates();
                });
                host.appendChild(b);
            });
        },

        renderTemplates: function () {
            var host = document.getElementById('itvx-tpl-grid');
            if (!host) return;
            host.textContent = '';
            var self = this;
            var list = this.templates().filter(function (tp, i) {
                if (self.activeCategory !== 'all' && tp.category !== self.activeCategory) return false;
                if (!self.templateSearch) return true;
                var hay = (tp.name + ' ' + (tp.description || '') + ' ' + tp.category).toLowerCase();
                return hay.indexOf(self.templateSearch) !== -1;
            });

            if (!list.length) {
                host.appendChild(this.el('div', 'itv-x-hint', t('tpl.none')));
                return;
            }

            list.forEach(function (tp) {
                var idx = self.templates().indexOf(tp);
                var card = self.el('button', 'itv-x-tpl');
                card.type = 'button';
                /* A locked card stays readable and stays clickable — see the
                   note in the stylesheet. Nothing is locked while
                   GATE_ENFORCED is false. */
                if (self.GATE_ENFORCED && !self.isPro && !self.isFreeTemplate(idx)) {
                    card.setAttribute('data-locked', 'true');
                }
                card.appendChild(self.el('div', 'itv-x-tpl-name', tp.name));
                card.appendChild(self.el('div', 'itv-x-tpl-desc', tp.description || ''));
                card.addEventListener('click', function () { self.applyTemplate(idx); });
                host.appendChild(card);
            });
        },

        /*
         * Which templates are free is POLICY and is not decided here. The
         * list is empty and the predicate is only consulted when
         * GATE_ENFORCED is true, so today every template is open.
         */
        FREE_TEMPLATE_INDEXES: [],
        isFreeTemplate: function (i) {
            return this.FREE_TEMPLATE_INDEXES.indexOf(i) !== -1;
        },

        applyTemplate: function (index) {
            var tp = this.templates()[index];
            if (!tp) return;
            if (this.GATE_ENFORCED && !this.isPro && !this.isFreeTemplate(index)) {
                this.showUnlockPrompt('template');
                return;
            }
            /* Deep clone so editing after applying cannot mutate the library
               — a template applied twice must give the same result both
               times. */
            var fresh = JSON.parse(JSON.stringify(tp.state));
            fresh.post.timestamp = fresh.post.timestamp || new Date().toISOString();
            this.state = Object.assign(this.defaultState(), fresh);
            this.syncFormFromState();
            this.saveState();
            this.renderExtrasEditors();
            this.renderReplyEditors();
            this.renderPreview();
            this.toast(t('tpl.applied'));
            this.track('template_applied', { source: 'template' });
        },

        /* =================================================================
         * Form <- state
         * ================================================================= */

        syncFormFromState: function () {
            var s = this.state;
            var set = function (id, v) {
                var el = document.getElementById(id);
                if (el && el.value !== String(v)) el.value = v;
            };
            var check = function (id, v) {
                var el = document.getElementById(id);
                if (el) el.checked = !!v;
            };
            set('itvx-name', s.author.name);
            set('itvx-handle', s.author.handle);
            set('itvx-badge', s.author.badge);
            check('itvx-protected', s.author.protected);
            set('itvx-text', s.post.text);
            set('itvx-source', s.post.source);
            set('itvx-dir', s.post.dir);
            check('itvx-altbadge', s.post.altBadge);
            ['replies', 'reposts', 'quotes', 'likes', 'bookmarks', 'views'].forEach(function (k) {
                set('itvx-m-' + k, s.metrics[k]);
            });
            ['liked', 'reposted', 'bookmarked', 'promoted', 'pinned'].forEach(function (k) {
                check('itvx-' + k, s.flags[k]);
            });
            var ts = document.getElementById('itvx-time');
            if (ts && s.post.timestamp) {
                var d = new Date(s.post.timestamp);
                if (!isNaN(d)) {
                    var pad = function (n) { return String(n).padStart(2, '0'); };
                    ts.value = d.getFullYear() + '-' + pad(d.getMonth() + 1) + '-' +
                        pad(d.getDate()) + 'T' + pad(d.getHours()) + ':' + pad(d.getMinutes());
                }
            }
            this.syncSeg('itvx-seg-theme', s.theme);
            this.syncSeg('itvx-seg-viewport', s.viewport);
            this.syncSeg('itvx-seg-layout', s.layout);
            this.updateCount();
        },

        syncSeg: function (id, value) {
            var g = document.getElementById(id);
            if (!g) return;
            Array.prototype.forEach.call(g.querySelectorAll('button'), function (b) {
                b.setAttribute('aria-pressed', String(b.getAttribute('data-value') === value));
            });
        },

        /* =================================================================
         * Export
         * ================================================================= */

        /*
         * The stage is rasterised at its TRUE width with the preview
         * transform removed, so the exported PNG is the same layout the
         * measurements were taken against rather than a scaled screenshot.
         */
        /*
         * EVERY ICON WAS MISSING FROM THE EXPORTED PNG, AND THE PREVIEW COULD
         * NOT SHOW IT.
         *
         * icon() builds <svg><use href="#itvx-i-..."></use></svg>, pointing at a
         * <symbol> in the page's sprite. The browser resolves that. html2canvas
         * does not - it has no <use> resolution - so it rasterises the <svg> as
         * empty. The export lost the verified badge, the Community Note icon and
         * all six action-bar icons: 8 icons on the default post.
         *
         * Nothing about this is visible in the tool. The preview is the browser
         * rendering the real DOM, so it looks perfect right up until you open the
         * file you downloaded. The export is a SECOND RENDERER and only the
         * downloaded image is evidence about it.
         *
         * So the <use> references are resolved by hand into the cloned document
         * html2canvas is about to read. Two details, both of which cost a wrong
         * fix first:
         *
         *   1. The presentation attributes live on the <symbol>, not the shapes:
         *      fill="none" stroke="currentColor" stroke-width="2". Copying only
         *      the children renders the Note icon as a SOLID BLACK DISC, because
         *      an un-inherited <circle> defaults to fill:black.
         *
         *   2. currentColor has to be RESOLVED to a real colour here. The cloned
         *      tree is rasterised outside the cascade that gave currentColor its
         *      meaning, so it must be read from the live element with
         *      getComputedStyle and written on as a literal, and set on each
         *      child rather than left to inherit.
         *
         * Verified by exporting and reading the resulting PNG, not by inspecting
         * the DOM - the DOM was never the thing that was wrong.
         */
        inlineSpriteIcons: function (doc) {
            var uses = doc.querySelectorAll('use');
            var done = 0;
            for (var i = 0; i < uses.length; i++) {
                var use = uses[i];
                var href = use.getAttribute('href') || use.getAttribute('xlink:href');
                if (!href || href.charAt(0) !== '#') continue;

                var sym = document.getElementById(href.slice(1));
                var svg = use.parentNode;
                if (!sym || !svg || String(svg.nodeName).toLowerCase() !== 'svg') continue;

                var colour = window.getComputedStyle(svg).color || '#000';
                var symFill = sym.getAttribute('fill');
                var symStroke = sym.getAttribute('stroke');
                var paint = function (v) { return v === 'currentColor' ? colour : v; };

                if (sym.getAttribute('viewBox') && !svg.getAttribute('viewBox')) {
                    svg.setAttribute('viewBox', sym.getAttribute('viewBox'));
                }
                var carry = ['stroke-width', 'stroke-linecap', 'stroke-linejoin', 'fill-rule', 'clip-rule'];
                for (var c = 0; c < carry.length; c++) {
                    if (sym.hasAttribute(carry[c]) && !svg.hasAttribute(carry[c])) {
                        svg.setAttribute(carry[c], sym.getAttribute(carry[c]));
                    }
                }
                svg.setAttribute('fill', symFill ? paint(symFill) : colour);
                if (symStroke) svg.setAttribute('stroke', paint(symStroke));

                use.parentNode.removeChild(use);
                for (var k = 0; k < sym.children.length; k++) {
                    var kid = sym.children[k].cloneNode(true);
                    if (!kid.hasAttribute('fill')) kid.setAttribute('fill', symFill ? paint(symFill) : colour);
                    if (symStroke && !kid.hasAttribute('stroke')) kid.setAttribute('stroke', paint(symStroke));
                    svg.appendChild(kid);
                }
                done++;
            }
            return done;
        },

        rasterise: function () {
            var self = this;
            return new Promise(function (resolve, reject) {
                if (typeof window.html2canvas !== 'function') {
                    reject(new Error('rasteriser-unavailable'));
                    return;
                }
                var prevTransform = self.stageScale.style.transform;
                var prevHeight = self.stageScale.style.height;
                self.stage.setAttribute('data-exporting', 'true');
                self.stageScale.style.transform = 'none';
                self.stageScale.style.height = 'auto';

                var restore = function () {
                    self.stage.removeAttribute('data-exporting');
                    self.stageScale.style.transform = prevTransform;
                    self.stageScale.style.height = prevHeight;
                };

                window.html2canvas(self.stage, {
                    scale: self.exportScale,
                    backgroundColor: null,
                    useCORS: true,
                    logging: false,
                    onclone: function (doc) { self.inlineSpriteIcons(doc); }
                }).then(function (canvas) {
                    restore();
                    resolve(canvas);
                }).catch(function (err) {
                    restore();
                    reject(err);
                });
            });
        },

        exportPNG: function () {
            var self = this;
            this.rasterise().then(function (canvas) {
                var link = document.createElement('a');
                link.download = 'x-post-' + Date.now() + '.png';
                link.href = canvas.toDataURL('image/png');
                link.click();
                self.toast(t('export.done'));
                /* The thing people came for. Outcome distinguishes a completed
                   export from one the rasteriser refused. */
                self.track('export_clicked', { source: 'export', outcome: 'clean' });
            }).catch(function () {
                self.toast(t('export.failed'), 'error');
                self.track('export_clicked', { source: 'export', outcome: 'error' });
            });
        },

        copyImage: function () {
            var self = this;
            this.rasterise().then(function (canvas) {
                if (!navigator.clipboard || !window.ClipboardItem) {
                    self.toast(t('export.failed'), 'error');
                    return;
                }
                canvas.toBlob(function (blob) {
                    navigator.clipboard.write([
                        new window.ClipboardItem({ 'image/png': blob })
                    ]).then(function () {
                        self.toast(t('export.copied'));
                    }).catch(function () {
                        self.toast(t('export.failed'), 'error');
                    });
                });
            }).catch(function () {
                self.toast(t('export.failed'), 'error');
            });
        },

        /*
         * Share link. Uploaded images are EXCLUDED from the payload — a
         * single avatar data URI is comfortably larger than the URL length
         * every browser and messaging app will carry, so including them
         * produces a link that silently truncates and restores a corrupted
         * state. Text, metrics, theme and structure round-trip.
         */
        shareState: function () {
            var s = JSON.parse(JSON.stringify(this.state));
            s.author.avatar = '';
            s.author.affiliateIcon = '';
            s.post.media = [];
            if (s.quoted) { s.quoted.author.avatar = ''; s.quoted.post.media = []; }
            (s.thread || []).forEach(function (p) { p.media = []; });
            (s.replies || []).forEach(function (r) { r.author.avatar = ''; r.post.media = []; });
            return s;
        },

        copyShareLink: function () {
            var self = this;
            var payload;
            try {
                payload = btoa(unescape(encodeURIComponent(JSON.stringify(this.shareState()))));
            } catch (e) {
                this.toast(t('export.failed'), 'error');
                return;
            }
            var url = location.origin + location.pathname + '?s=' + encodeURIComponent(payload);
            var done = function () { self.toast(t('export.linkCopied')); };
            if (navigator.clipboard && navigator.clipboard.writeText) {
                navigator.clipboard.writeText(url).then(done).catch(function () {
                    self.toast(t('export.failed'), 'error');
                });
            } else {
                self.toast(t('export.failed'), 'error');
            }
        },

        loadFromURL: function () {
            try {
                var p = new URLSearchParams(location.search).get('s');
                if (!p) return;
                var json = decodeURIComponent(escape(atob(p)));
                var s = JSON.parse(json);
                if (!s || !s.author || !s.post) return;
                this.state = Object.assign(this.defaultState(), s);
                this.syncFormFromState();
                this.renderExtrasEditors();
                this.renderReplyEditors();
                this.renderPreview();
            } catch (e) { /* a mangled link falls back to the saved state */ }
        },

        /* ================================================================= */

        toast: function (msg, kind) {
            var el = document.getElementById('itvx-toast');
            if (!el) return;
            el.textContent = msg;
            el.setAttribute('data-kind', kind || 'info');
            el.setAttribute('data-show', 'true');
            clearTimeout(this.toastTimer);
            this.toastTimer = setTimeout(function () {
                el.setAttribute('data-show', 'false');
            }, 2600);
        }
    };

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', function () { window.itvXPost.init(); });
    } else {
        window.itvXPost.init();
    }
})(window, document);
